/**
 * Student dashboard at /me — upcoming lessons (with Enter Classroom magic link),
 * past lessons (with Watch Recording), profile, and resources.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api";
import { useStudentAuth } from "../StudentAuthContext";
import RecordingPlayer from "../components/RecordingPlayer";
import { MaterialCard, MaterialUploader } from "../components/Materials";

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString([], {
    weekday: "long", day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function relTime(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  const min = Math.round(ms / 60000);
  if (min < -60 * 24) return `${Math.round(-min / 60 / 24)} days ago`;
  if (min < -60) return `${Math.round(-min / 60)}h ago`;
  if (min < 0) return `${-min} min ago`;
  if (min < 60) return `in ${min} min`;
  if (min < 60 * 24) return `in ${Math.round(min / 60)}h`;
  return `in ${Math.round(min / 60 / 24)} days`;
}

export default function Me() {
  const { status, student, token, logout, setStudent } = useStudentAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (status === "anon") {
      navigate("/login", { state: { next: "/me" }, replace: true });
      return;
    }
    if (status !== "auth") return;
    (async () => {
      try {
        const d = await api.studentMyLessons(token);
        setData(d);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [status, token, navigate]);

  function signOut() {
    // Navigate first so the anon-redirect effect in this very component
    // doesn't kick in and override us with a /login redirect.
    navigate("/", { replace: true });
    logout();
  }

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-20 md:pt-24 pb-24 px-6 md:px-10" data-testid="me-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
              Your account
            </div>
            <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
              {student?.name ? `Hola, ${student.name}.` : "Hola."}
            </h1>
            <p className="mt-2 text-sm text-[#1C1C1C]/60">{student?.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/book"
              data-testid="me-book-link"
              className="px-4 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
            >
              Book a lesson →
            </Link>
            <button
              onClick={signOut}
              data-testid="me-signout-btn"
              className="px-4 py-2 text-[#1C1C1C]/60 text-xs tracking-[0.18em] uppercase font-medium hover:text-[#1C1C1C] transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 bg-[#B83A3A]/10 text-[#B83A3A] rounded-xl text-sm" data-testid="me-error">
            {error}
          </div>
        )}

        {/* Quick-nav: jump to a section without scrolling */}
        <nav className="mb-2 flex flex-wrap gap-x-5 gap-y-2 text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55" data-testid="me-section-nav">
          <a href="#upcoming" className="hover:text-[#1C1C1C]">Lessons</a>
          <a href="#profile" className="hover:text-[#1C1C1C]">Profile</a>
          <a href="#resources" className="hover:text-[#1C1C1C]">Resources</a>
        </nav>

        {loading ? (
          <div className="text-[#1C1C1C]/60">Loading your lessons…</div>
        ) : (
          <>
            <Section
              id="upcoming"
              title="Upcoming lessons"
              testid="me-upcoming"
              empty="No lessons booked yet."
              emptyCtaLabel="Find a slot"
              emptyCtaTo="/book"
              items={data.upcoming}
              renderItem={(l) => (
                <article
                  key={l.id}
                  data-testid={`me-upcoming-item-${l.id}`}
                  className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-5 md:p-6 flex flex-wrap items-center justify-between gap-4"
                >
                  <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-[#1C1C1C]/55 mb-1">
                      {relTime(l.scheduled_start)}
                    </div>
                    <div className="font-display text-xl md:text-2xl text-[#1C1C1C] tracking-tight">{l.title}</div>
                    <div className="text-sm text-[#1C1C1C]/60 mt-1">{fmtDateTime(l.scheduled_start)}</div>
                  </div>
                  <Link
                    to={`/c/${l.student_passcode}`}
                    data-testid={`me-enter-${l.id}`}
                    className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
                  >
                    Enter classroom →
                  </Link>
                </article>
              )}
            />

            <Section
              id="past"
              title="Past lessons"
              testid="me-past"
              empty="Your past lessons will appear here."
              items={data.past}
              renderItem={(l) => (
                <article
                  key={l.id}
                  data-testid={`me-past-item-${l.id}`}
                  className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-5 md:p-6"
                >
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#1C1C1C]/55 mb-1">
                    {relTime(l.scheduled_start)}
                  </div>
                  <div className="font-display text-xl md:text-2xl text-[#1C1C1C] tracking-tight">{l.title}</div>
                  <div className="text-sm text-[#1C1C1C]/60 mt-1">{fmtDateTime(l.scheduled_start)}</div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40 mt-2">
                    See recording and shared materials below
                  </div>
                </article>
              )}
            />

            <ProfileSection
              student={student}
              token={token}
              onUpdated={(s) => setStudent(s)}
            />

            <ResourcesSection
              token={token}
              lessons={data}
            />
          </>
        )}
      </div>
    </main>
  );
}

const SPANISH_LEVELS = [
  { v: "A1", label: "A1 · Beginner" },
  { v: "A2", label: "A2 · Elementary" },
  { v: "B1", label: "B1 · Intermediate" },
  { v: "B2", label: "B2 · Upper-intermediate" },
  { v: "C1", label: "C1 · Advanced" },
  { v: "C2", label: "C2 · Mastery" },
];

function ProfileSection({ student, token, onUpdated }) {
  const [editing, setEditing] = useState(false);
  return (
    <section id="profile" className="mt-10 md:mt-14 scroll-mt-24" data-testid="me-profile">
      <div className="flex items-end justify-between gap-4 mb-4">
        <h2 className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60">
          My profile
        </h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            data-testid="me-profile-edit-btn"
            className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] transition-colors"
          >
            Edit →
          </button>
        )}
      </div>
      {editing ? (
        <ProfileForm
          student={student}
          token={token}
          onSaved={(s) => { onUpdated(s); setEditing(false); }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <ProfileRead student={student} />
      )}
      <PasswordCard token={token} />
    </section>
  );
}

function ProfileRead({ student }) {
  const items = [
    ["Email", student?.email],
    ["Full name", student?.name],
    ["Age", student?.age],
    ["Country", student?.country],
    ["City", student?.city],
    ["Native language", student?.native_language],
    ["Spanish level",
      student?.spanish_level
        ? (SPANISH_LEVELS.find((x) => x.v === student.spanish_level)?.label || student.spanish_level)
        : null],
    ["Phone", student?.phone],
  ];
  return (
    <div className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 md:p-8" data-testid="me-profile-read">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-1">
              {label}
            </dt>
            <dd className={`text-sm ${value ? "text-[#1C1C1C]" : "text-[#1C1C1C]/35 italic"}`}>
              {value || "Not set"}
            </dd>
          </div>
        ))}
      </dl>
      <div className="mt-6 pt-5 border-t border-[#1C1C1C]/10">
        <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-1">
          Learning goals
        </div>
        <p className={`text-sm leading-relaxed ${student?.learning_goals ? "text-[#1C1C1C]" : "text-[#1C1C1C]/35 italic"}`}>
          {student?.learning_goals || "Not set — tell us what brought you to Spanish."}
        </p>
      </div>
      <div className="mt-5">
        <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-1">
          Interests
        </div>
        <p className={`text-sm leading-relaxed ${student?.interests ? "text-[#1C1C1C]" : "text-[#1C1C1C]/35 italic"}`}>
          {student?.interests || "Not set — list topics, hobbies, anything you'd like lessons to touch on."}
        </p>
      </div>
    </div>
  );
}

function ProfileForm({ student, token, onSaved, onCancel }) {
  const initial = useMemo(() => ({
    name: student?.name || "",
    age: student?.age ?? "",
    country: student?.country || "",
    city: student?.city || "",
    native_language: student?.native_language || "",
    spanish_level: student?.spanish_level || "",
    phone: student?.phone || "",
    learning_goals: student?.learning_goals || "",
    interests: student?.interests || "",
  }), [student]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      // Build payload: empty strings → null, age → number
      const body = {};
      for (const k of Object.keys(form)) {
        const v = form[k];
        if (k === "age") {
          if (v === "" || v === null || v === undefined) continue;
          body[k] = Number(v);
        } else if (typeof v === "string") {
          body[k] = v.trim() || null;
        } else {
          body[k] = v;
        }
      }
      const updated = await api.studentUpdateMe(token, body);
      onSaved(updated);
      toast.success("Profile updated");
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 md:p-8 space-y-5" data-testid="me-profile-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Full name">
          <input data-testid="me-profile-name" required maxLength={120} value={form.name} onChange={(e) => set("name", e.target.value)} className="me-input" />
        </Field>
        <Field label="Age">
          <input data-testid="me-profile-age" type="number" min={0} max={120} value={form.age} onChange={(e) => set("age", e.target.value)} className="me-input" />
        </Field>
        <Field label="Country">
          <input data-testid="me-profile-country" maxLength={120} value={form.country} onChange={(e) => set("country", e.target.value)} className="me-input" />
        </Field>
        <Field label="City">
          <input data-testid="me-profile-city" maxLength={120} value={form.city} onChange={(e) => set("city", e.target.value)} className="me-input" />
        </Field>
        <Field label="Native language">
          <input data-testid="me-profile-native-language" maxLength={80} value={form.native_language} onChange={(e) => set("native_language", e.target.value)} className="me-input" />
        </Field>
        <Field label="Spanish level (CEFR)">
          <select data-testid="me-profile-spanish-level" value={form.spanish_level} onChange={(e) => set("spanish_level", e.target.value)} className="me-input">
            <option value="">— select —</option>
            {SPANISH_LEVELS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
          </select>
        </Field>
        <Field label="Phone (optional)">
          <input data-testid="me-profile-phone" maxLength={40} value={form.phone} onChange={(e) => set("phone", e.target.value)} className="me-input" />
        </Field>
      </div>
      <Field label="Learning goals">
        <textarea data-testid="me-profile-goals" rows={4} maxLength={2000} value={form.learning_goals} onChange={(e) => set("learning_goals", e.target.value)} className="me-input resize-y" placeholder="What do you want to use Spanish for? (travel, family, work, exam prep, conversations…)" />
      </Field>
      <Field label="Interests">
        <textarea data-testid="me-profile-interests" rows={3} maxLength={2000} value={form.interests} onChange={(e) => set("interests", e.target.value)} className="me-input resize-y" placeholder="Topics, hobbies, films, music — anything you'd like lessons to touch on." />
      </Field>
      {error && <div className="text-sm text-[#B83A3A]" data-testid="me-profile-error">{error}</div>}
      <div className="flex flex-wrap gap-3">
        <button data-testid="me-profile-submit" type="submit" disabled={saving} className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors">
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-3 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors">
          Cancel
        </button>
      </div>
      <style>{`.me-input{width:100%;padding:.7rem 1rem;background:#FAF9F6;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:0.95rem;outline:none}.me-input:focus{border-color:rgba(28,28,28,0.4);background:white}`}</style>
    </form>
  );
}

function PasswordCard({ token }) {
  const [open, setOpen] = useState(false);
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ kind: "", text: "" });

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setMsg({ kind: "", text: "" });
    try {
      await api.studentChangePassword(token, curr, next);
      setMsg({ kind: "ok", text: "Password changed." });
      setCurr(""); setNext("");
      toast.success("Password changed");
    } catch (err) {
      setMsg({ kind: "err", text: err.message || "Could not change password." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 md:p-8" data-testid="me-password-card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60">Password</div>
          <p className="text-sm text-[#1C1C1C]/60 mt-1">Keep your account secure with a strong password.</p>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="me-password-open-btn" className="text-[10px] tracking-[0.24em] uppercase text-[#1C1C1C]/60 hover:text-[#1C1C1C] transition-colors">
            Change password →
          </button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="me-password-form">
          <Field label="Current password">
            <input data-testid="me-password-current" type="password" required autoComplete="current-password" value={curr} onChange={(e) => setCurr(e.target.value)} className="me-input" />
          </Field>
          <Field label="New password (min 8 characters)">
            <input data-testid="me-password-new" type="password" required minLength={8} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className="me-input" />
          </Field>
          {msg.text && (
            <div data-testid="me-password-msg" className={`text-sm ${msg.kind === "ok" ? "text-emerald-700" : "text-[#B83A3A]"}`}>
              {msg.text}
            </div>
          )}
          <div className="flex gap-3">
            <button data-testid="me-password-submit" type="submit" disabled={saving} className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors">
              {saving ? "Updating…" : "Update password"}
            </button>
            <button type="button" onClick={() => { setOpen(false); setMsg({ kind: "", text: "" }); }} className="px-5 py-3 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ResourcesSection({ token, lessons }) {
  const [materials, setMaterials] = useState(null);
  const [error, setError] = useState("");
  const [player, setPlayer] = useState(null); // { lessonId, title }
  const [deletingRec, setDeletingRec] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const past = lessons?.past || [];
  const upcoming = lessons?.upcoming || [];
  const all = [...past, ...upcoming];

  // Index materials by lesson_id for grouped rendering.
  const byLesson = useMemo(() => {
    const map = {};
    (materials || []).forEach((m) => {
      (map[m.lesson_id] = map[m.lesson_id] || []).push(m);
    });
    return map;
  }, [materials]);

  async function refresh() {
    try {
      const list = await api.studentListMaterials(token);
      setMaterials(list);
    } catch (e) {
      setError(e.message || "Could not load materials.");
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleUpload(lessonId, file) {
    setUploadingFor(lessonId);
    try {
      await api.studentUploadMaterial(token, lessonId, file);
      await refresh();
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed.");
      throw e;
    } finally {
      setUploadingFor(null);
    }
  }

  async function handleDeleteMaterial(material) {
    await api.studentDeleteMaterial(token, material.id);
    await refresh();
  }

  async function handleDeleteRecording(lesson) {
    if (!window.confirm(`Delete the recording of "${lesson.title}"? This cannot be undone.`)) return;
    setDeletingRec(lesson.id);
    try {
      await api.studentDeleteRecording(token, lesson.id);
      toast.success("Recording deleted");
      // mark it gone locally so the card hides immediately
      lesson.recording_status = "none";
      // Force a re-render
      setMaterials((m) => (m ? [...m] : m));
    } catch (e) {
      toast.error(e.message || "Could not delete.");
    } finally {
      setDeletingRec(null);
    }
  }

  // Past lessons with a ready recording get a play card; past lessons
  // without one yet get a "processing" card so students aren't left
  // wondering where their recording went.
  const readyRecordings = past.filter((l) => l.recording_status === "ready");
  const pendingRecordings = past.filter((l) => l.recording_status !== "ready");

  return (
    <section id="resources" className="mt-10 md:mt-14 scroll-mt-24" data-testid="me-resources">
      <h2 className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
        My resources
      </h2>

      {error && (
        <div className="mb-4 px-4 py-3 bg-[#B83A3A]/10 text-[#B83A3A] rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* ---------- Recordings shelf ---------- */}
      <div className="mb-6" data-testid="me-recordings-shelf">
        <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 mb-3">
          Recordings · view only
        </h3>
        {readyRecordings.length === 0 && pendingRecordings.length === 0 ? (
          <div className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 text-sm text-[#1C1C1C]/55" data-testid="me-recordings-empty">
            Lesson recordings appear here a few minutes after the call ends.
          </div>
        ) : (
          <div className="space-y-3">
            {readyRecordings.map((l) => (
              <article
                key={l.id}
                data-testid={`me-recording-${l.id}`}
                className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-4 md:p-5 flex flex-wrap items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#1C1C1C]/5 text-[#1C1C1C]/70 flex items-center justify-center text-base shrink-0">
                  ▶
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base md:text-lg text-[#1C1C1C] tracking-tight truncate">{l.title}</div>
                  <div className="text-[11px] tracking-[0.06em] text-[#1C1C1C]/55 mt-0.5">
                    {fmtDateTime(l.scheduled_start)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`me-recording-watch-${l.id}`}
                    onClick={() => setPlayer({ lessonId: l.id, title: l.title })}
                    className="px-3 py-2 bg-[#1C1C1C] text-[#FAF9F6] rounded-lg text-[10px] tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
                  >
                    Watch →
                  </button>
                  <button
                    type="button"
                    disabled={deletingRec === l.id}
                    onClick={() => handleDeleteRecording(l)}
                    data-testid={`me-recording-delete-${l.id}`}
                    className="px-3 py-2 text-[#1C1C1C]/55 text-[10px] tracking-[0.18em] uppercase hover:text-[#B83A3A] disabled:opacity-40 transition-colors"
                  >
                    {deletingRec === l.id ? "…" : "Delete"}
                  </button>
                </div>
              </article>
            ))}
            {pendingRecordings.map((l) => (
              <article
                key={l.id}
                data-testid={`me-recording-pending-${l.id}`}
                className="bg-[#FAF9F6] border border-[#1C1C1C]/8 border-dashed rounded-2xl p-4 md:p-5 flex flex-wrap items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#1C1C1C]/5 text-[#1C1C1C]/35 flex items-center justify-center text-base shrink-0">
                  ◷
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base md:text-lg text-[#1C1C1C]/70 tracking-tight truncate">{l.title}</div>
                  <div className="text-[11px] tracking-[0.06em] text-[#1C1C1C]/45 mt-0.5">
                    {fmtDateTime(l.scheduled_start)}
                  </div>
                </div>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40">
                  {l.recording_status === "recording" ? "Recording…" : "Processing — check back soon"}
                </span>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* ---------- Materials shelf ---------- */}
      <div data-testid="me-materials-shelf">
        <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 mb-3">
          Materials · per lesson
        </h3>
        {all.length === 0 ? (
          <div className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 text-sm text-[#1C1C1C]/55" data-testid="me-materials-no-lessons">
            Once you have a lesson booked, you'll be able to upload notes, homework, audio recordings — anything you want to keep tied to that lesson.
          </div>
        ) : (
          <div className="space-y-5">
            {all.map((l) => {
              const items = byLesson[l.id] || [];
              return (
                <div
                  key={l.id}
                  data-testid={`me-materials-lesson-${l.id}`}
                  className="bg-[#FAF9F6] rounded-2xl border border-[#1C1C1C]/8 p-4 md:p-5"
                  style={{ borderColor: "rgba(28,28,28,0.08)" }}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                    <div>
                      <div className="font-display text-base text-[#1C1C1C] tracking-tight">{l.title}</div>
                      <div className="text-[11px] tracking-[0.06em] text-[#1C1C1C]/55">
                        {fmtDateTime(l.scheduled_start)}
                      </div>
                    </div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#1C1C1C]/40">
                      {items.length === 0 ? "No materials yet" : `${items.length} file${items.length === 1 ? "" : "s"}`}
                    </div>
                  </div>
                  <div className="space-y-2 mb-3">
                    {items.map((m) => (
                      <MaterialCard
                        key={m.id}
                        material={m}
                        fileUrl={api.studentMaterialFileUrl(token, m.id)}
                        onDelete={handleDeleteMaterial}
                      />
                    ))}
                  </div>
                  <MaterialUploader
                    compact
                    busy={uploadingFor === l.id}
                    onUpload={(f) => handleUpload(l.id, f)}
                    data-testid={`me-upload-${l.id}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {player && (
        <RecordingPlayer
          src={api.studentRecordingStreamUrl(token, player.lessonId)}
          title={player.title}
          onClose={() => setPlayer(null)}
        />
      )}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

function Section({ id, title, testid, items, renderItem, empty, emptyCtaLabel, emptyCtaTo }) {
  return (
    <section id={id} className="mt-10 md:mt-14 scroll-mt-24" data-testid={testid}>
      <h2 className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
        {title}
      </h2>
      {items.length === 0 ? (
        <div
          data-testid={`${testid}-empty`}
          className="bg-white border border-[#1C1C1C]/10 rounded-2xl p-8 text-center"
        >
          <p className="text-[#1C1C1C]/60 mb-4">{empty}</p>
          {emptyCtaLabel && emptyCtaTo && (
            <Link
              to={emptyCtaTo}
              className="inline-block px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
            >
              {emptyCtaLabel} →
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">{items.map(renderItem)}</div>
      )}
    </section>
  );
}
